import React, {useState} from "react";
import {theme, Typography} from "@ekultur/vm";
import {createUseStyles} from "react-jss";
import {Alternative} from "./Alternative";
import {BLOCK_UNLOCKED, useProgressDispatch} from "../progress/progressContext";
import {RetryButton} from "./RetryButton";
import {useContrastApproved} from "../helpers/useContrast";
import {useBackgroundColor} from "../Container/useBackgroundColor";

const useStyle = createUseStyles(({ colors }) => ({
  quizBlock: {
    display: "flex",
    flexDirection: "column",
    margin: ({ margin }) => margin,
    padding: ({ padding }) => padding,
    color: ({ color }) => color || colors.black,
    textAlign: ({ textAlign }) => textAlign,
    fontFamily: ({ font }) => font,
    borderRadius: ({ borderRadius }) => borderRadius,
    border: "none",
  },
  legend: {
    padding: 0,
    margin: "0 0 16px 0",
    display: "table",
    width: "100%",
    boxSizing: "border-box",
  },
  alternatives: {
    display: "flex",
    flexDirection: "column",
    flex: 3,
  },
  feedback: {
    margin: "12px 0",
    fontWeight: 500,
  },
  explanation: {
    marginTop: 8,
    fontWeight: "normal",
  },
}));

export const QuizBlock = ({
  containerId,
  sectionId,
  blockId,
  block,
}) => {
  const {
    question,
    alternatives,
    answer,
    margin,
    padding,
    textAlign,
    font,
    borderRadius,
    explanation,
  } = block;

  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const progressDispatch = useProgressDispatch();
  const backgroundColor = useBackgroundColor({
    sectionId: sectionId,
    containerId: containerId
  });

  const contrastApproved = useContrastApproved(
      backgroundColor !== undefined
          ? backgroundColor.substring(0, 7)
          : theme.colors.white,
      theme.colors.white
  );

  const classes = useStyle({
    margin,
    padding,
    color: contrastApproved ? "white" : "black",
    textAlign,
    font,
    borderRadius,
  });

  const handleClick = option => {
    setSelectedAnswer(option);
    if (option === answer) {
      progressDispatch({
        type: BLOCK_UNLOCKED,
        payload: {
          sectionId: sectionId,
          containerId: containerId,
          blockId: blockId
        }
      });
    }
  };

  const handleRetry = () => setSelectedAnswer(null);
  const solved = selectedAnswer === answer;
  const questionId = `quiz-question-${sectionId}-${containerId}-${blockId}`;

  return (
      <fieldset
          className={classes.quizBlock}
          role="radiogroup"
          aria-labelledby={questionId}
      >
        <legend id={questionId} className={classes.legend}>
          <Typography tag="span" size="xl" bold>
            {question}
          </Typography>
        </legend>
        <div className={classes.alternatives}>
          {alternatives.map(({alternative, text}) => (
              <Alternative
                  key={alternative}
                  alternative={alternative}
                  selectedAnswer={selectedAnswer}
                  onAnswerSelect={handleClick}
                  text={text}
                  solved={solved}
                  whiteBorder={contrastApproved}
              />
          ))}
        </div>
        <div role="status" aria-live="polite" className={classes.feedback}>
          {solved && (
              <div>
                <span>Riktig svar!</span>
                {explanation && (
                    <p className={classes.explanation}>{explanation}</p>
                )}
              </div>
          )}
          {selectedAnswer && !solved && (
              <span>Feil svar. Prøv igjen!</span>
          )}
        </div>
        <RetryButton
            onClick={handleRetry}
            show={Boolean(selectedAnswer && !solved)}
        />
      </fieldset>
  );
};
